
import {
  CreateReducer,
  CreateAction,
} from '../../store/utils/store'

import {
  getProjectHandlers,
} from '../../store/utils/api'

import {
  RBACState,
  ReducerHandler,
  ThunkHandler,
} from '../../types/store'

import {
  PayloadAddUser,
  PayloadUpdateUser,
  PayloadDeleteUser,
  PayloadAddOrg,
  PayloadAddOrgWithProfile,
  PayloadUpdateOrg,
  PayloadDeleteOrg,
  PayloadOrgRoleSummary,
  PayloadUserRoleSummary,
} from '../../types/rbac'

import {
  IData_MagicTag_Summary,
} from '@kaidam/fc-shared/src/types/schema/magic_tag'

import {
  IQuery_Org_Scope,
  IQuery_Id,
  IQuery_Project_Org,
  IMetadata,
  IQueryData,
  IDataBooleanResponse,
} from '@kaidam/fc-shared/src/types/utils'

import {
  IRecord_User,
  IRecord_User_Core,
  IRecord_User_Password_Plain,
} from '@kaidam/fc-shared/src/types/schema/user'

import {
  IRecord_Org,
  IRecord_Org_Core,  
} from '@kaidam/fc-shared/src/types/schema/org'

import {
  IData_Org_With_Profile,
  IQuery_RBAC_UserExists,
} from '@kaidam/fc-shared/src/types/rbac'

import {
  INode_Org,
  INode_User,
  ITree_Role,
} from '@kaidam/fc-shared/src/types/schema/tree'

import {
  IExport_Org,
  IExport_User,
} from '@kaidam/fc-shared/src/types/schema/exports'

import {
  IData_UserSummary_FlatRoleObject,
} from '@kaidam/fc-shared/src/types/schema/user_summary'

import {
  IData_AppEvent_Type_Summary,
  IRecord_AppEvent_With_UserOrg,
} from '@kaidam/fc-shared/src/types/schema/app_event'

import {
  IQuery_AppEvent_List,
} from '@kaidam/fc-shared/src/types/app_event'

import {
  IData_Paginated_Query_Wrapper,
  IData_Paginated_Results_Wrapper,
} from '@kaidam/fc-shared/src/types/pagination'

import {
  getPaginationQueryParams,
} from '@kaidam/fc-shared/src/utils/pagination'

import {
  stripObjectValues,
} from '@kaidam/fc-shared/src/utils/object'

import {
  NetworkThunk,
} from '../utils/network'

import {
  PAGINATION_RESULTS_DEFAULT,
} from '@kaidam/fc-shared/src/constants'

import rbacSelectors from '../selectors/rbac'
import snackbarActions from './snackbar'
import projectActions from './project'

const prefix = 'rbac'

/*

  initial state

*/
const initialState: RBACState = {
  org: null,
  users: [],
  roleTree: null,
  roleSummary: [],
  eventTypes: [],
  eventData: [],
  eventPagination: PAGINATION_RESULTS_DEFAULT,
  profile: null,
  profilesLoaded: false,
  profiles: [],
  magicTags: [],
}

/*

  reducers

*/
const setOrg: ReducerHandler<RBACState, INode_Org> = (state, action) => {
  state.org = action.payload
}

const setUsers: ReducerHandler<RBACState, INode_User[]> = (state, action) => {
  state.users = action.payload
}

const setRoleTree: ReducerHandler<RBACState, ITree_Role> = (state, action) => {
  state.roleTree = action.payload
}

const setRoleSummary: ReducerHandler<RBACState, IData_UserSummary_FlatRoleObject[]> = (state, action) => {
  state.roleSummary = action.payload
}

const setEventTypes: ReducerHandler<RBACState, IData_AppEvent_Type_Summary[]> = (state, action) => {
  state.eventTypes = action.payload
}

const setEventData: ReducerHandler<RBACState, IData_Paginated_Results_Wrapper<IRecord_AppEvent_With_UserOrg>> = (state, action) => {
  const {
    data,
    pagination,
  } = action.payload
  state.eventData = data
  state.eventPagination = pagination
}

const setProfile: ReducerHandler<RBACState, IData_Org_With_Profile> = (state, action) => {
  state.profile = action.payload
}

const setProfiles: ReducerHandler<RBACState, IData_Org_With_Profile[]> = (state, action) => {
  state.profiles = action.payload
}

const setProfilesLoaded: ReducerHandler<RBACState, boolean> = (state, action) => {
  state.profilesLoaded = action.payload
}

const setMagicTags: ReducerHandler<RBACState, IData_MagicTag_Summary[]> = (state, action) => {
  state.magicTags = action.payload
}

/*

  thunks

*/


/*

  org

*/
const loadOrg: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<INode_Org>(`/rbac/org`)
  dispatch(actions.setOrg(data))
}


const addOrg: ThunkHandler<PayloadAddOrg, boolean> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.post<IExport_Org, IRecord_Org>(`/rbac/org/${query.org}/orgs`, query.data)
  await dispatch(loadOrg())
  dispatch(snackbarActions.setSuccess(`org added`))
  return true
}

const addOrgWithProfile: ThunkHandler<PayloadAddOrgWithProfile, boolean> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const org = await handlers.post<IExport_Org, IRecord_Org>(`/rbac/org/${query.org}/orgs`, query.data)
  const data = await handlers.put<IMetadata, IData_Org_With_Profile>(`/rbac/org/${org.id}/profile`, query.profile)
  await dispatch(loadOrg())
  await dispatch(actions.loadProfiles())
  dispatch(snackbarActions.setSuccess(`org added`))
  return true
}

const updateOrg: ThunkHandler<PayloadUpdateOrg, boolean> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.put<Partial<IRecord_Org_Core>, IRecord_Org>(`/rbac/org/${query.org}`, query.data)
  await dispatch(loadOrg())
  dispatch(snackbarActions.setSuccess(`org updated`))
  return true
}

const deleteOrg: ThunkHandler<PayloadDeleteOrg, boolean> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.delete<IRecord_Org>(`/rbac/org/${query.parent}/orgs/${query.org}`)
  await dispatch(loadOrg())
  dispatch(snackbarActions.setSuccess(`org deleted`))
  return true
}

/*

  user

*/
const loadUsers: ThunkHandler<string, void> = (orgid) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<INode_User[]>(`/rbac/org/${orgid}/users`)
  dispatch(actions.setUsers(data))
}

const addUser: ThunkHandler<PayloadAddUser, boolean> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.post<IExport_User, IRecord_User>(`/rbac/org/${query.org}/users`, query.data)
  await dispatch(loadUsers(query.org))
  await dispatch(loadOrg())
  dispatch(snackbarActions.setSuccess(`user added`))
  return true
}

const userExists: ThunkHandler<IQuery_RBAC_UserExists, IDataBooleanResponse> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const result = await handlers.get<IDataBooleanResponse>(`/rbac/user_exists`, {
    params: {
      email: query.email,
    }
  })
  return result
}

const updateUser: ThunkHandler<PayloadUpdateUser, boolean> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.put<Partial<IRecord_User_Core & IRecord_User_Password_Plain>, IRecord_User>(`/rbac/org/${query.org}/users/${query.user}`, query.data)
  await dispatch(loadUsers(query.org))
  await dispatch(loadOrg())
  dispatch(snackbarActions.setSuccess(`user updated`))
  return true
}

const moveUser: ThunkHandler<{
  org: string,
  destorg: string,
  user: string,
}, boolean> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.put(`/rbac/org/${query.org}/users/${query.user}/move/${query.destorg}`)
  await dispatch(loadUsers(query.org))
  await dispatch(loadOrg())
  dispatch(snackbarActions.setSuccess(`user moved`))
  return true
}

const deleteUser: ThunkHandler<PayloadDeleteUser, boolean> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.delete<IRecord_User>(`/rbac/org/${query.org}/users/${query.user}`)
  await dispatch(loadUsers(query.org))
  await dispatch(loadOrg())
  dispatch(snackbarActions.setSuccess(`user deleted`))
  return true
}


/*

  role tree

*/
const loadRoleTree: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<ITree_Role>(`/rbac/role_tree`)
  dispatch(actions.setRoleTree(data))
}

const loadRoleSummaryOrg: ThunkHandler<PayloadOrgRoleSummary, void> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IData_UserSummary_FlatRoleObject[]>(`/rbac/org/${query.org}/role_summary`)
  dispatch(actions.setRoleSummary(data))
}

const loadRoleSummaryUser: ThunkHandler<PayloadUserRoleSummary, void> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IData_UserSummary_FlatRoleObject[]>(`/rbac/org/${query.org}/users/${query.user}/role_summary`)
  dispatch(actions.setRoleSummary(data))
}

/*

  events

*/
const loadEventTypes: ThunkHandler<IQuery_Org_Scope, void> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IData_AppEvent_Type_Summary[]>(`/rbac/event_types/${query.org}/${query.scope}`)
  dispatch(actions.setEventTypes(data))
}

const loadEvents: ThunkHandler<IData_Paginated_Query_Wrapper<IQuery_AppEvent_List & IQuery_Org_Scope>, void> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const {
    pagination,
    query,
  } = payload
  const paginationQueryParams = getPaginationQueryParams(pagination)
  const [searchQueryParams, [org, scope]] = stripObjectValues(query, ['org', 'scope'])
  if(!org) throw new Error(`org required for loadEvents`)
  if(!scope) throw new Error(`scope required for loadEvents`)
  const allQueryParams = Object.assign({}, paginationQueryParams, searchQueryParams)
  const data = await handlers.get<IData_Paginated_Results_Wrapper<IRecord_AppEvent_With_UserOrg>>(`/rbac/events/${org}/${scope}`, {
    params: allQueryParams,
  })
  dispatch(actions.setEventData(data))
}

/*

  profiles

*/
const loadProfile: ThunkHandler<IQuery_Id, void> = (query) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IData_Org_With_Profile>(`/rbac/org/${query.id}/profile`)
  dispatch(actions.setProfile(data))
}

const loadProfiles: ThunkHandler<void, boolean> = () => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IData_Org_With_Profile[]>(`/rbac/profiles`, {
    params: {
      view_all: 'yes',
    }
  })
  dispatch(actions.setProfiles(data))
  dispatch(actions.setProfilesLoaded(true))
  return true
}

const ensureProfiles: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  dispatch(loadProfiles())
}

const updateProfile: ThunkHandler<IQueryData<IQuery_Id, IMetadata>, boolean> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.put<IMetadata, IData_Org_With_Profile>(`/rbac/org/${payload.query.id}/profile`, payload.data)
  await dispatch(actions.loadProfiles())
  dispatch(snackbarActions.setSuccess('profile updated'))
  return true
}

const updateProfiles: ThunkHandler<any[], void> = (data) => async (dispatch, getState) => {
  const profileMap = rbacSelectors.profileMap(getState())
  const submitData = data.map(item => {
    const submitProfile = Object.assign({}, item)
    delete(submitProfile.id)
    delete(submitProfile.core_name) 
    const org = profileMap[item.id]
    return Object.assign({}, org, {
      profile: submitProfile,
    })
  })
  const handlers = getProjectHandlers(getState)
  await handlers.put(`/rbac/all_profiles`, {
    data: submitData,
  })
  await dispatch(actions.loadProfiles())
  dispatch(snackbarActions.setSuccess('profiles updated'))
}

// generic route for updating an org's project app data
// the permissions are handled by the server - the "path" will be whastever
// server route that proxies to rbac is
// for example PUT /reporting/:orgid
// will require the "reporting:admin" role
// the api route is the special case
// the rbac controller to update the org meta
// and this client controller are generic
const updateOrgAppData: ThunkHandler<IQueryData<{path: string}, any>, void> = (payload) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.put(payload.query.path, payload.data)
  // reload the orgs
  await dispatch(projectActions.loadUserSummary(''))
  dispatch(snackbarActions.setSuccess('data updated'))
}

/*

  magic tags

*/
const loadMagicTags: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IData_MagicTag_Summary[]>(`/rbac/magic_tag_summary`)
  dispatch(actions.setMagicTags(data))
}

/*

  personas

*/
const updateUserPersonas: ThunkHandler<IQueryData<{
  org: string,
  user: string,
}, string[]>, boolean> = ({
  query: {
    org,
    user,
  },
  data,
}) => async (dispatch, getState) => {
  const handlers = getProjectHandlers(getState)
  await handlers.put(`/rbac/org/${org}/users/${user}/personas`, {
    personas: data,
  })
  dispatch(snackbarActions.setSuccess('personas updated'))
  return true
}

/*

  collections

*/
const reducers = {
  setOrg,
  setUsers,
  setRoleTree,
  setRoleSummary,
  setEventTypes,
  setEventData, 
  setProfile,
  setProfiles,
  setProfilesLoaded,
  setMagicTags,
}

export const actions = {
  setOrg: CreateAction(prefix, `setOrg`, reducers.setOrg),
  setUsers: CreateAction(prefix, `setUsers`, reducers.setUsers),
  setRoleTree: CreateAction(prefix, `setRoleTree`, reducers.setRoleTree),
  setRoleSummary: CreateAction(prefix, `setRoleSummary`, reducers.setRoleSummary),
  setEventTypes: CreateAction(prefix, `setEventTypes`, reducers.setEventTypes),
  setEventData: CreateAction(prefix, `setEventData`, reducers.setEventData),
  setProfile: CreateAction(prefix, `setProfile`, reducers.setProfile),
  setProfiles: CreateAction(prefix, `setProfiles`, reducers.setProfiles),
  setProfilesLoaded: CreateAction(prefix, `setProfilesLoaded`, reducers.setProfilesLoaded),
  setMagicTags: CreateAction(prefix, `setMagicTags`, reducers.setMagicTags),
  loadOrg: NetworkThunk(prefix, 'loadOrg', loadOrg, {
    snackbar: true,
  }),
  loadUsers: NetworkThunk(prefix, 'loadUsers', loadUsers, {
    snackbar: true,
  }),
  loadRoleTree: NetworkThunk(prefix, 'loadRoleTree', loadRoleTree, {
    snackbar: true,
  }),
  loadRoleSummaryOrg: NetworkThunk(prefix, 'loadRoleSummaryOrg', loadRoleSummaryOrg, {
    snackbar: true,
  }),
  loadRoleSummaryUser: NetworkThunk(prefix, 'loadRoleSummaryUser', loadRoleSummaryUser, {
    snackbar: true,
  }),
  addUser: NetworkThunk(prefix, 'addUser', addUser, {
    snackbar: true,
    globalLoading: {
      message: 'adding user...'
    }
  }),
  userExists: NetworkThunk(prefix, 'userExists', userExists, {
    snackbar: true,
    globalLoading: {
      message: 'searching for user...'
    }
  }),
  updateUser: NetworkThunk(prefix, 'updateUser', updateUser, {
    snackbar: true,
    globalLoading: {
      message: 'updating user...'
    }
  }),
  moveUser: NetworkThunk(prefix, 'moveUser', moveUser, {
    snackbar: true,
    globalLoading: {
      message: 'moving user...'
    }
  }),
  deleteUser: NetworkThunk(prefix, 'deleteUser', deleteUser, {
    snackbar: true,
    globalLoading: {
      message: 'deleting user...'
    }
  }),
  addOrg: NetworkThunk(prefix, 'addOrg', addOrg, {
    snackbar: true,
    globalLoading: {
      message: 'adding org...'
    }
  }),
  addOrgWithProfile: NetworkThunk(prefix, 'addOrgWithProfile', addOrgWithProfile, {
    snackbar: true,
    globalLoading: {
      message: 'adding org...'
    }
  }),
  updateOrg: NetworkThunk(prefix, 'updateOrg', updateOrg, {
    snackbar: true,
    globalLoading: {
      message: 'updating org...'
    }
  }),
  deleteOrg: NetworkThunk(prefix, 'deleteOrg', deleteOrg, {
    snackbar: true,
    globalLoading: {
      message: 'deleting org...'
    }
  }),
  loadEventTypes: NetworkThunk(prefix, 'loadEventTypes', loadEventTypes, {
    snackbar: true,
  }),
  loadEvents: NetworkThunk(prefix, 'loadEvents', loadEvents, {
    snackbar: true,
  }),
  loadProfile: NetworkThunk(prefix, 'loadProfile', loadProfile, {
    snackbar: true,
  }),
  loadProfiles: NetworkThunk(prefix, 'loadProfiles', loadProfiles, {
    snackbar: true,
  }),
  ensureProfiles: NetworkThunk(prefix, 'ensureProfiles', ensureProfiles, {
    snackbar: true,
  }),
  updateProfile: NetworkThunk(prefix, 'updateProfile', updateProfile, {
    snackbar: true,
    globalLoading: {
      message: 'updating profile...'
    }
  }),
  updateOrgAppData: NetworkThunk(prefix, 'updateOrgAppData', updateOrgAppData, {
    snackbar: true,
    globalLoading: {
      message: 'updating data...'
    }
  }),
  updateProfiles: NetworkThunk(prefix, 'updateProfiles', updateProfiles, {
    snackbar: true,
    globalLoading: {
      message: 'updating profiles...'
    }
  }),
  loadMagicTags: NetworkThunk(prefix, 'loadMagicTags', loadMagicTags, {
    snackbar: true,
  }),
  updateUserPersonas: NetworkThunk(prefix, 'updateUserPersonas', updateUserPersonas, {
    snackbar: true,
    globalLoading: {
      message: 'updating user personas...'
    }
  }),
}

export const reducer = CreateReducer<RBACState>({
  prefix,
  initialState,
  reducers,
})

export default actions
